import { type MachineBase, type MachineStatus } from "@packsize/schemas/machines";

import { errorSeverity } from "@/constants";

export type MachineDisplayStatus = {
	status: MachineStatus;
	hasError: boolean;
	hasWarning: boolean;
};

export type MachineMessage = {
	message: string;
	severity: (typeof errorSeverity)[keyof typeof errorSeverity];
};

export const machineIsInErrorStatus = (machine: MachineBase): boolean => {
	return machine.status === "Error";
};

export const machineHasWarning = (machine: MachineBase): boolean => {
	return machine.workstations.some((ws) => ws.warnings.length > 0);
};

export const getMachineDisplayStatus = (machine: MachineBase): MachineDisplayStatus => {
	const hasError = machineIsInErrorStatus(machine);
	const hasWarning = machineHasWarning(machine);

	return {
		status: machine.status,
		hasError,
		hasWarning,
	};
};

export const getErrorStopReasonMachineMessage = (machine: MachineBase): MachineMessage[] => {
	if (!machineIsInErrorStatus(machine)) {
		return [];
	}

	const stopReasonCodes = new Set<number>();
	const stopReasonMessages: MachineMessage[] = [];

	for (const workstation of machine.workstations) {
		const stopReason = workstation.stopReason;
		if (!stopReason.isError || stopReason.code <= 0 || stopReasonCodes.has(stopReason.code)) {
			continue;
		}

		stopReasonCodes.add(stopReason.code);
		stopReasonMessages.push({
			message: getTranslationId(
				machine.machineType,
				errorSeverity.error,
				stopReason.code,
				stopReason.message,
				stopReason.arguments,
			),
			severity: errorSeverity.error,
		});
	}

	return stopReasonMessages;
};

export const getWarningMachineMessages = (machine: MachineBase): MachineMessage[] => {
	const warningCodes = new Set<number>();
	const warningMessages: MachineMessage[] = [];

	for (const workstation of machine.workstations) {
		for (const warning of workstation.warnings) {
			if (warningCodes.has(warning.code)) {
				continue;
			}

			warningCodes.add(warning.code);
			warningMessages.push({
				message: getTranslationId(
					machine.machineType,
					errorSeverity.warning,
					warning.code,
					warning.message,
					warning.arguments,
				),
				severity: errorSeverity.warning,
			});
		}
	}

	return warningMessages;
};

const getTranslationId = (
	machineType: string,
	severity: string,
	code: number,
	message: string,
	messageArguments: string[],
): string => {
	if (machineType === "X5") {
		const parameter = code === 1 && severity === errorSeverity.warning ? messageArguments[1] : undefined;
		return `${machineType} ${severity} ${code}${parameter !== undefined ? `-${parameter}` : ""}`;
	} else if (machineType === "X7") {
		return `${message !== "" ? message : "Unknown"}`;
	} else {
		return `${machineType} ${severity} ${code}`;
	}
};
